import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import animateScrollTo from 'animated-scroll-to';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay, faPause, faBan,
} from '@fortawesome/free-solid-svg-icons';
import exact from 'prop-types-exact';
import A1GA from '../../../helpers/A1GA';
import Button from '../../../common/button/Button';
import ErrorPage from '../../../common/errorPage/ErrorPage';
import Spinner from '../../../common/Spinner/Spinner';
import HeaderTags from '../../../common/headerTags/HeaderTags';

const ClientsSinglePageComponent = ({
    slug, clientsSingleSlug, clientsSingleData, isLoadingClientsData, playerStatus, currentSources,
    getClientsSingleDataIfNeeded, playMediaPlayer, pauseMediaPlayer, playMediaFromSourceWithMeta, showSpotModal,
}) => {
    const location = useLocation();

    useEffect(() => {
        if (location.hash !== '#Spots') {
            // If there is no Spots hash, scroll normally to the top
            // If there is the Spots hash, the browser will scroll automatically to the spots section
            // Note: In Chrome this takes very long, but I assume it is the Brawser default...
            animateScrollTo(0, { speed: 700 });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (clientsSingleSlug !== slug) {
            // fetch the data for this client
            getClientsSingleDataIfNeeded(slug);
        }
    }, [slug, clientsSingleSlug, getClientsSingleDataIfNeeded]);

    const _logToGA = useCallback((logType, spot) => {
        let action;
        let label;

        if (logType === 'clientLinkClicked') {
            action = 'client_link_clicked';

            label = `client: ${clientsSingleData.name}, link: ${clientsSingleData.link}`;
        } else if (logType === 'spotModalOpened') {
            action = 'spot_modal_opened';

            label = `client: ${clientsSingleData.name}, spot: ${spot.name}`;
        } else if (logType === 'audioPlay') {
            action = 'spot_audio_play';

            label = `client: ${clientsSingleData.name}, spot: ${spot.name}`;
        }

        A1GA.event({
            category: 'Clients',
            action,
            label,
        });
    }, [clientsSingleData]);

    const _playPauseSpot = useCallback((index) => {
        const spot = clientsSingleData.spots[index];

        const spotSrc = `https://a1media.antena1.com.br/audio/comerciais/${spot.audioFile}`;
        const currentMusicSrc = currentSources[0]?.src;

        if ((playerStatus === 'playing') && (currentMusicSrc === spotSrc)) {
            // If the user clicks on the same spot while it is playing, pause it
            return pauseMediaPlayer();
        }

        if ((playerStatus === 'paused') && (currentMusicSrc === spotSrc)) {
            // If the user clicks on the same spot while it is paused, resume it
            return playMediaPlayer();
        }

        // otherwise start the (new) spot
        const albumCover = spot.imageFile || 'defaultSpot.png';
        const coverImage = `https://a1media.antena1.com.br/image/comerciais/1000x1000/${albumCover}`;
        const meta = {
            artist: clientsSingleData.name,
            cover: coverImage,
            song: spot.name,
        };
        const sources = {
            src: spotSrc,
            type: 'audio/mp3',
        };

        _logToGA('audioPlay', spot);

        return playMediaFromSourceWithMeta(sources, meta);
    }, [clientsSingleData, currentSources, playerStatus, _logToGA, playMediaFromSourceWithMeta, pauseMediaPlayer, playMediaPlayer]);

    const _mapSpots = useCallback((spots) => spots.map((spot, index) => {
        // const imageFile = spot.imageFile != null ? spot.imageFile : 'defaultSpot.png';
        const spotSrc = spot.audioFile ? `https://a1media.antena1.com.br/audio/comerciais/${spot.audioFile}` : false;
        const currentMusicSrc = currentSources[0]?.src;
        // If the player is currently playing or loading, check if the src of the spot matches the currentSources
        const faIcon = (currentMusicSrc === spotSrc && (playerStatus === 'playing' || playerStatus === 'loading')) ? faPause : faPlay;

        return (
            <div key={`spot-${spot.slug}`} className="spotItem">
                <div className="spotItemContent">
                    {spotSrc ?
                        <Button className="spotPlayButton" title="Toque o audio desse spot" onClick={() => _playPauseSpot(index)}>
                            <FontAwesomeIcon icon={faIcon} width="15" />
                        </Button> :
                        <Button className="spotPlayButton" title="Audio indisponível" disabled>
                            <FontAwesomeIcon icon={faBan} width="15" />
                        </Button>}
                    {/*
                        <div className="spotImage">
                            <img src={`https://a1media.antena1.com.br/image/comerciais/50x50/${imageFile}`} alt={`Capa do spot ${spot.name}`} />
                        </div>
                        */}
                    <h3>
                        {spot.name}
                    </h3>
                </div>
                <Button
                    className="spotButton"
                    title="Saiba mais sobre este spot"
                    onClick={() => {
                        _logToGA('spotModalOpened', spot);

                        showSpotModal(spot.slug);
                    }}
                >
                    Transcrito
                </Button>
            </div>);
    }), [_logToGA, _playPauseSpot, currentSources, playerStatus, showSpotModal]);

    if (isLoadingClientsData) {
        // not loaded yet
        return (
            <div className="clientsSlingleContainer">
                <Spinner />
            </div>
        );
    }

    if (!isLoadingClientsData && (!clientsSingleData || Object.keys(clientsSingleData).length === 0)) {
        // there was no client found for the given slug
        return <ErrorPage
            errorNumber={404}
            backLink="/clientes"
            title="NÃO ACHAMOS ESSE CLIENTE :("
            message="Verifique se o link foi digitado corretamente."
            buttonText="Clique aqui para ver todos os nossos clientes"
        />;
    }

    const client = clientsSingleData;
    const headerTags =
    {
        title: `${client.name} - Clientes Antena 1`, // max 60 char in total, ' | Antena 1' (11 chars) is automatically added by the Headers Tag and is truncated
        meta:
        {
            description: `Saiba mais sobre ${client.name}`,
            keywords: `${client.name}, anuncie, anunciante, cliente, cliente Antena 1`,
            'og:title': `${client.name} - Clientes Antena 1`,
            'og:description': `Saiba mais sobre ${client.name}`,
            'og:image': 'https://a1media.antena1.com.br/image/share/clientes.png',
        },
    };

    const image = `https://a1media.antena1.com.br/image/clientes/400x400/${client.imageFile !== null ? client.imageFile : 'defaultCliente.png'}`;

    return (
        <div className="clientsSlingleContainer">
            <HeaderTags title={headerTags.title} meta={headerTags.meta} />
            <Button className="backToPage" title="Veja todos os clientes" to="/clientes">
                <img src="/keyboard-right-arrow-button.svg" alt="Ícone seta para a esquerda" />
                {' '}
                Veja todos os clientes.
            </Button>
            <header>
                <h1>Nossos clientes</h1>
                <p className="pageSubtitle">Saiba mais sobre as marcas que anunciam em nossa programação</p>
            </header>

            <div className="clientsContent">
                <div className="clientsSingleImage">
                    <img src={image} alt={`Logo de ${client.name}`} />
                </div>

                <div className="clientsSingleDetails">
                    <h2>
                        {client.name}
                    </h2>

                    <div
                        className="clientsSingleDescription"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: client.description }}
                    />


                    {client.locations.length > 0 &&
                        <React.Fragment>
                            <h2>Praças</h2>
                            <div className="locationsContainer">
                                {
                                    // eslint-disable-next-line react/jsx-one-expression-per-line
                                    client.locations.map((_location) => <div key={_location} className="locationsItem">{_location}</div>)
                                }
                            </div>
                        </React.Fragment>}

                    {client.spots && client.spots.length > 0 &&
                        <React.Fragment>
                            <h2 id="Spots">Spots</h2>
                            <div className="spotsList">
                                {_mapSpots(client.spots)}
                            </div>
                        </React.Fragment>}
                    <a title="Ir para a página Anuncie Conosco" className="btnAnuncie" href="https://comercial.antena1.com.br/">Anuncie Conosco</a>
                </div>

            </div>

            {/* client.link &&
                <Button className="goToClientPage" title="Vai para a página desse cliente" href={client.link} onClick={() => _logToGA('clientLinkClicked')}>
                    Página do cliente
                </Button> */}
        </div>
    );
};

ClientsSinglePageComponent.propTypes = exact({
    slug: PropTypes.string.isRequired,
    clientsSingleSlug: PropTypes.string.isRequired,
    clientsSingleData: PropTypes.object.isRequired,
    getClientsSingleDataIfNeeded: PropTypes.func.isRequired,
    isLoadingClientsData: PropTypes.bool.isRequired,
    playMediaFromSourceWithMeta: PropTypes.func.isRequired,
    pauseMediaPlayer: PropTypes.func.isRequired,
    playMediaPlayer: PropTypes.func.isRequired,
    currentSources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            type: PropTypes.string,
        }),
    ),
    playerStatus: PropTypes.string.isRequired,
    showSpotModal: PropTypes.func.isRequired,
});

export default ClientsSinglePageComponent;
